import { Button, Modal, Popconfirm, Space, Tooltip, Typography } from "antd";
import { KeyRound, Smartphone } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { SecurityOverview, securityApi } from "../../backend/security";
import { useAuth } from "../../contexts/Auth";
import { isPasskeySupported } from "../../utils/webauthn";
import SecurityEnrollmentModal from "./SecurityEnrollmentModal";

const SNOOZE_DAYS = 7;

function snoozeKey(profileId?: string) {
    return profileId ? `mfaPromptSnoozedUntil:${profileId}` : undefined;
}

function isSnoozed(profileId?: string): boolean {
    const key = snoozeKey(profileId);
    // sessionStorage dies with the tab, so the durable copy is keyed by user.
    const values = [sessionStorage.getItem("mfaPromptSnoozedUntil"), key ? localStorage.getItem(key) : null];

    return values.some(value => {
        const until = value ? new Date(value).getTime() : NaN;
        return Number.isFinite(until) && until > Date.now();
    });
}

export default function EnrollmentPrompt() {
    const { profile } = useAuth();
    const location = useLocation();
    const onSecurityPage = location.pathname.startsWith("/account/security");
    const [open, setOpen] = useState(false);
    const [method, setMethod] = useState<"totp" | "passkey">();
    const [overview, setOverview] = useState<SecurityOverview>();

    useEffect(() => {
        // Account security already offers enrollment; prompting there would double up.
        if (onSecurityPage || sessionStorage.getItem("mfaPromptChecked") === "true" || isSnoozed(profile?.id)) {
            return;
        }
        sessionStorage.setItem("mfaPromptChecked", "true");

        securityApi.getOverview().then(overview => {
            setOverview(overview);
            // Never open a prompt whose buttons would all be unavailable.
            if (overview.shouldPrompt
                && overview.authenticationFactors.length === 0
                && (overview.totpAvailable || overview.passkeysAvailable)) {
                setOpen(true);
            }
        }).catch(() => undefined);
    }, [onSecurityPage, profile?.id]);

    const snooze = () => {
        setOpen(false);
        const until = new Date(new Date().getTime() + SNOOZE_DAYS * 24 * 60 * 60 * 1000).toISOString();
        sessionStorage.setItem("mfaPromptSnoozedUntil", until);
        const key = snoozeKey(profile?.id);
        if (key) {
            localStorage.setItem(key, until);
        }
    };

    const dismissForever = async () => {
        setOpen(false);
        try {
            await securityApi.updatePreferences(true);
        } catch {
            // The server preference is the durable state; snooze locally when it fails.
            snooze();
        }
    };

    const begin = (nextMethod: "totp" | "passkey") => {
        setOpen(false);
        setMethod(nextMethod);
    };

    return (
        <>
            <Modal open={open} title="Add two-step verification" footer={null} closable maskClosable={false} onCancel={snooze}>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Typography.Paragraph type="secondary">
                        Add an authenticator app or a passkey. It takes about a minute and protects your account if your password leaks.
                    </Typography.Paragraph>
                    {overview?.totpAvailable && (
                        <Button block size="large" icon={<Smartphone size={18} />} onClick={() => begin("totp")}>
                            Authenticator app
                        </Button>
                    )}
                    {overview?.passkeysAvailable && (
                        <Tooltip title={isPasskeySupported() ? undefined : "This browser doesn't support passkeys"}>
                            <Button block size="large" icon={<KeyRound size={18} />} disabled={!isPasskeySupported()} onClick={() => begin("passkey")}>
                                Passkey
                            </Button>
                        </Tooltip>
                    )}
                    <Space style={{ justifyContent: "space-between", width: "100%" }}>
                        <Button type="link" onClick={snooze}>Remind me later</Button>
                        <Popconfirm
                            title="Turn off the reminder?"
                            description="You can turn it back on from Account security."
                            okText="Turn off"
                            onConfirm={dismissForever}
                        >
                            <Button type="text" size="small">
                                <Typography.Text type="secondary">Don't remind me again</Typography.Text>
                            </Button>
                        </Popconfirm>
                    </Space>
                </Space>
            </Modal>
            <SecurityEnrollmentModal
                open={Boolean(method)}
                initialMethod={method}
                onClose={() => setMethod(undefined)}
                onComplete={() => undefined}
            />
        </>
    );
}
